import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable, {
  headerCellStyle,
  bodyCellStyle,
} from "./components/SelectableEnrollmentTable";

interface BulkAssignCourseModalProps {
  show: boolean;
  onHide: () => void;
  /** "module" lists non-elective courses; "elective" lists IsElective courses. */
  mode: "module" | "elective";
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "assigned" | "skipped" | "not_found" | "error";
  reason?: string;
  error?: string;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    assigned:  { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkAssignCourseModal: React.FC<BulkAssignCourseModalProps> = ({
  show,
  onHide,
  mode,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const noun = mode === "elective" ? "Elective" : "Module";

  const [courses, setCourses] = useState<Array<{ id: number; name: string }>>([]);
  const [terms, setTerms] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [courseId, setCourseId] = useState<number | "">("");
  const [termId, setTermId] = useState<number | "">("");
  const [remarks, setRemarks] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  const {
    enrollments,
    selectedIds,
    selectedEnrollmentIds,
    toggle,
    toggleAll,
    allSelected,
  } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    EnrollmentService.getLookups()
      .then((resp) => {
        if (cancelled) return;
        const d = resp.data?.data || {};
        setCourses((mode === "elective" ? d.electives : d.modules) || []);
        setTerms(d.terms || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, mode]);

  const handleClose = () => {
    setCourseId("");
    setTermId("");
    setRemarks("");
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!courseId) {
      setError(`Pick ${mode === "elective" ? "an elective" : "a module"}.`);
      return;
    }
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkAssignCourse({
        enrollmentIds: selectedEnrollmentIds,
        courseId: Number(courseId),
        termId: termId ? Number(termId) : null,
        remarks: remarks.trim() || null,
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || `Bulk assign ${noun.toLowerCase()} failed`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? `Assign ${noun} — Results` : `Assign ${noun} (Bulk)`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {loadingLookups ? (
              <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                <i className="fa fa-spinner fa-spin" /> Loading options…
              </div>
            ) : (
              <>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Form.Group className="mb-3" style={{ flex: "1 1 240px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                      {noun} <span style={{ color: "var(--status-danger)" }}>*</span>
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={courseId}
                      onChange={(e) => setCourseId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">Select {noun.toLowerCase()}…</option>
                      {courses.map((co) => (
                        <option key={co.id} value={co.id}>
                          {co.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3" style={{ flex: "1 1 200px" }}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Term</Form.Label>
                    <Form.Select
                      size="sm"
                      value={termId}
                      onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">(none)</option>
                      {terms.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional"
                  />
                </Form.Group>

                <div
                  style={{
                    fontSize: 12,
                    color: "#854d0e",
                    background: "#fefce8",
                    border: "1px solid var(--status-warning-border)",
                    borderRadius: 6,
                    padding: "8px 10px",
                    marginBottom: 10,
                  }}
                >
                  Enrolls each student in the selected {noun.toLowerCase()} (course mapping). Fee /
                  revenue / installment accrual is not applied here.
                </div>

                <SelectableEnrollmentTable
                  enrollments={enrollments}
                  selectedIds={selectedIds}
                  allSelected={allSelected}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                  disabled={submitting}
                  caption={`Enrollments (${selectedEnrollmentIds.length} of ${enrollments.length} selected)`}
                />
              </>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.assigned ?? 0}</strong> assigned
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "assigned" && (
                          <span style={{ color: "var(--text-body)" }}>{noun} assigned</span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_assigned"
                              ? `Already has this ${noun.toLowerCase()}`
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || loadingLookups || !courseId || selectedEnrollmentIds.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Applying…
                </>
              ) : (
                <>Assign {noun} ({selectedEnrollmentIds.length})</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAssignCourseModal;
